import { ChevronDown, ChevronRight } from "lucide-react";
import * as React from "react";

import * as AccordionPrimitive from "@radix-ui/react-accordion";

import { cn } from "~/lib/utils";

const Accordion = AccordionPrimitive.Root;

interface AccordionItemProps
  extends React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item> {
  className?: string;
  "data-testid": string | undefined;
}

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  AccordionItemProps
>(({ className, "data-testid": dataTestId, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn("border-b", className)}
    data-testid={dataTestId || "AccordionItem"}
    {...props}
  />
));
AccordionItem.displayName = "AccordionItem";

interface AccordionTriggerProps
  extends React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> {
  className?: string;
  arrowDirection?: string;
  highlightExpandedItem?: boolean;
  children: React.ReactNode;
  icon?: React.ReactElement | null;
  "data-testid": string | undefined;
}

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  AccordionTriggerProps
>(
  (
    {
      className,
      icon = null,
      children,
      arrowDirection,
      highlightExpandedItem = false,
      "data-testid": dataTestId,
      ...props
    },
    ref,
  ) => (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        ref={ref}
        className={cn(
          "flex flex-1 items-center justify-between py-4 font-medium transition-all hover:underline [&[data-state=open]>svg]:rotate-90",
          highlightExpandedItem && "[&[data-state=open]]:bg-wavy/30",
          arrowDirection &&
            arrowDirection === "down" &&
            "[&[data-state=open]>svg]:rotate-180",
          className,
        )}
        {...props}
      >
        {children}
        {icon ? (
          // A custom icon (e.g. the quiz Lock) replaces the chevron and must NOT
          // inherit the chevron's rotate-on-open. The trigger's rotate variant
          // targets the DIRECT svg child (`[&[data-state=open]>svg]:rotate-*`),
          // so wrap the icon to keep it out of that selector.
          <span className="flex shrink-0">{icon}</span>
        ) : arrowDirection === "down" ? (
          <ChevronDown
            className="h-5 w-5 shrink-0 py-0.5 text-primary transition-transform duration-200"
            strokeWidth={3}
            data-testid={dataTestId || "accordion-ChevronDown"}
          />
        ) : (
          <ChevronRight
            className="h-5 w-5 shrink-0 py-0.5 text-primary transition-transform duration-200"
            strokeWidth={3}
            data-testid={dataTestId || "accordion-ChevronRight"}
          />
        )}
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  ),
);
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

interface AccordionContentProps
  extends React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content> {
  className?: string;
  children: React.ReactNode;
  "data-testid": string | undefined;
}

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  AccordionContentProps
>(({ className, children, "data-testid": dataTestId, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden text-sm transition-all"
    data-testid={dataTestId || "AccordionContent"}
    {...props}
  >
    <div className={cn("pb-4 pt-0", className)}>{children}</div>
  </AccordionPrimitive.Content>
));

AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger };
